import React from "react";
import { type Control, Controller } from "react-hook-form";

// Time picker constants
const HOURS = Array.from({ length: 12 }, (_, i) =>
  (i + 1).toString().padStart(2, "0")
);

// Minute interval options - used by getMinuteOptions based on minuteInterval prop
const MINUTES_15 = ["00", "15", "30", "45"];
const MINUTES_5 = [
  "00",
  "05",
  "10",
  "15",
  "20",
  "25",
  "30",
  "35",
  "40",
  "45",
  "50",
  "55",
];
const MINUTES_1 = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0")
);

const AMPM = ["AM", "PM"] as const;

export interface TimeValue {
  hour: string;
  minute: string;
  ampm: "AM" | "PM";
}

type MinuteInterval = 1 | 5 | 15;

interface TimePickerProps {
  /** Current time value */
  value?: TimeValue;
  /** Callback when time changes */
  onChange?: (value: TimeValue) => void;
  /** Minute interval for the dropdown (1, 5, or 15). Default: 15 */
  minuteInterval?: MinuteInterval;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Optional label */
  label?: string;
  /** For react-hook-form integration */
  control?: Control<any>;
  /** Field name for react-hook-form */
  name?: string;
  /** Validation rules for react-hook-form */
  rules?: Record<string, unknown>;
  /** Additional className for the container */
  className?: string;
}

const getMinuteOptions = (interval: MinuteInterval): string[] => {
  switch (interval) {
    case 1:
      return MINUTES_1;
    case 5:
      return MINUTES_5;
    case 15:
    default:
      return MINUTES_15;
  }
};

const defaultValue: TimeValue = {
  hour: "12",
  minute: "00",
  ampm: "AM",
};

const selectClassName =
  "block w-18 py-1.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed";

const ampmClassName =
  "block w-20 py-1.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed";

const TimePickerCore: React.FC<{
  value: TimeValue;
  onChange: (value: TimeValue) => void;
  minuteInterval: MinuteInterval;
  disabled?: boolean;
}> = ({ value, onChange, minuteInterval, disabled }) => {
  const minutes = getMinuteOptions(minuteInterval);

  const handleHourChange = (hour: string) => {
    onChange({ ...value, hour });
  };

  const handleMinuteChange = (minute: string) => {
    onChange({ ...value, minute });
  };

  const handleAmPmChange = (ampm: "AM" | "PM") => {
    onChange({ ...value, ampm });
  };

  return (
    <div className="flex items-center gap-1">
      <select
        value={value.hour}
        onChange={e => handleHourChange(e.target.value)}
        disabled={disabled}
        className={selectClassName}>
        {HOURS.map(h => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-gray-500">:</span>
      <select
        value={value.minute}
        onChange={e => handleMinuteChange(e.target.value)}
        disabled={disabled}
        className={selectClassName}>
        {minutes.map(m => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        value={value.ampm}
        onChange={e => handleAmPmChange(e.target.value as "AM" | "PM")}
        disabled={disabled}
        className={ampmClassName}>
        {AMPM.map(ap => (
          <option key={ap} value={ap}>
            {ap}
          </option>
        ))}
      </select>
    </div>
  );
};

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  minuteInterval = 15,
  disabled = false,
  label,
  control,
  name,
  rules,
  className,
}) => {
  const isControlled = !!control && !!name;

  const handleChange = (newValue: TimeValue) => {
    onChange?.(newValue);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      {isControlled ? (
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field }) => (
            <TimePickerCore
              value={field.value ?? defaultValue}
              onChange={field.onChange}
              minuteInterval={minuteInterval}
              disabled={disabled}
            />
          )}
        />
      ) : (
        <TimePickerCore
          value={value ?? defaultValue}
          onChange={handleChange}
          minuteInterval={minuteInterval}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default TimePicker;
